import { sameOriginApiBase, isBrowserRuntime } from './sameOriginApiBase';

/** Must match server/seo-discovery/ssrApiBridge.js */
const SSR_SEO_API_GLOBAL = '__PROMOTIX_SSR_SEO_API__';

const getSsrSeoApi = () => {
  if (isBrowserRuntime()) {
    return null;
  }
  if (typeof globalThis !== 'undefined' && globalThis[SSR_SEO_API_GLOBAL]) {
    return globalThis[SSR_SEO_API_GLOBAL];
  }
  if (typeof global !== 'undefined' && global[SSR_SEO_API_GLOBAL]) {
    return global[SSR_SEO_API_GLOBAL];
  }
  return null;
};

/**
 * Prefer in-process SSR bridge (no HTTP). Falls back to same-origin fetch for
 * browser / when the bridge is not registered.
 */
const viaBridgeOrFetch = (methodName, args, httpPath, query) => {
  const api = getSsrSeoApi();
  if (api && typeof api[methodName] === 'function') {
    return Promise.resolve(api[methodName](...args));
  }
  return getJson(httpPath, query);
};

const getJson = (path, query = {}) => {
  const base = sameOriginApiBase();
  if (!base) {
    return Promise.reject(new Error('Cannot determine API base URL'));
  }
  const qs = new URLSearchParams(query).toString();
  const url = `${base}/api/seo-discovery${path}${qs ? `?${qs}` : ''}`;
  return fetch(url, {
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
  }).then(res =>
    // Read as text first so a non-JSON body (e.g. an HTML 403/502 error page from a
    // CDN/WAF edge, or an upstream crash) throws a clean, descriptive error instead
    // of an opaque "Unexpected token '<'" SyntaxError that aborts SSR.
    res.text().then(text => {
      let body = null;
      try {
        body = text ? JSON.parse(text) : null;
      } catch (parseErr) {
        const err = new Error(
          `Non-JSON response (${res.status}) from ${url}: ${String(text).slice(0, 120)}`
        );
        err.status = res.status;
        throw err;
      }
      if (!res.ok) {
        const err = new Error(body?.error || `Request failed (${res.status})`);
        err.status = res.status;
        throw err;
      }
      return body;
    })
  );
};

export const fetchSeoVenuePage = venueSlug =>
  viaBridgeOrFetch(
    'fetchSeoVenuePage',
    [venueSlug],
    `/venues/${encodeURIComponent(venueSlug)}`
  );

export const fetchSeoVenueSubpage = (venueSlug, subpageSlug) =>
  viaBridgeOrFetch(
    'fetchSeoVenueSubpage',
    [venueSlug, subpageSlug],
    `/venues/${encodeURIComponent(venueSlug)}/${encodeURIComponent(subpageSlug)}`
  );

export const fetchSeoVenuesIndex = (query = {}) =>
  viaBridgeOrFetch('fetchSeoVenuesIndex', [query], '/venues', query);

export const fetchSeoCitiesIndex = (query = {}) =>
  viaBridgeOrFetch('fetchSeoCitiesIndex', [query], '/cities', query);

export const fetchSeoCityPage = citySlug =>
  viaBridgeOrFetch('fetchSeoCityPage', [citySlug], `/cities/${encodeURIComponent(citySlug)}`);

export const fetchSeoCitySubpage = (citySlug, subpageSlug) =>
  viaBridgeOrFetch(
    'fetchSeoCitySubpage',
    [citySlug, subpageSlug],
    `/cities/${encodeURIComponent(citySlug)}/${encodeURIComponent(subpageSlug)}`
  );

export const fetchSeoPerformersIndex = (query = {}) =>
  viaBridgeOrFetch('fetchSeoPerformersIndex', [query], '/performers', query);

export const fetchSeoPerformerPage = performerSlug =>
  viaBridgeOrFetch(
    'fetchSeoPerformerPage',
    [performerSlug],
    `/performers/${encodeURIComponent(performerSlug)}`
  );

export const fetchSeoPerformerSubpage = (performerSlug, subpageSlug) =>
  viaBridgeOrFetch(
    'fetchSeoPerformerSubpage',
    [performerSlug, subpageSlug],
    `/performers/${encodeURIComponent(performerSlug)}/${encodeURIComponent(subpageSlug)}`
  );
